import React, { FC, useState, useEffect } from 'react';
import { SCUIButton, SCUILoading } from '../../atomic/atoms';
import SCUIDownloadButton from '../../atomic/atoms/SCUIDownloadButton/SCUIDownloadButton';
import { SCUIContent } from '../../atomic/organisms';
import { useTranslation } from 'react-i18next';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../atomic/molecules';
import { Grid, Tooltip } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { RefundRestQuery } from '../../services/query';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import TimeHelper from '../../utils/time';
import Swal from 'sweetalert2';
import Helper from '../../utils/helper';
import { PetitionPDF } from './PetitionPDF';
import { NoResultFoundComponent } from '../CustomerManagement/components/NoResultFoundComponent/NoResultFoundComponent';
import { PRLV2 } from './petitionRefundListV2.classes';

export const PetitionRefundListV2: FC<any> = () => {
    const backofficeUrl = import.meta.env.VITE_APPURL!;
    const { t } = useTranslation();
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [selectedRowIds, setSelectedRowIds] = useState<any[]>([]);
    const [pageSize, setPageSize] = useState(100);
    const [pageNumber, setPageNumber] = useState(0);
    const [selectedRowsForDownload, setSelectedForDownload] = useState<any[]>([]);
    const [selectedRowsAmount, setSelectedRowsAmount] = useState('');
    const filterInput = { Take: 100, Skip: 0 };
    const [loading, setLoading] = useState(false);
    const [pdfDownload, setPdfDownload] = useState<boolean>(false);
    const [createPetitionButtonDisabled, setCreatePetitionButtonDisabled] = useState<boolean>(true);
    const [datas, setDatas] = useState<any>();
    const [hiddenExcelColumn, setHiddenExcelColumn] = useState<string[]>([]);
    const [pdfComponent, setPdfComponent] = useState<React.ReactNode | null>(null);
    const [bankTxtDownloading, setBankTxtDownloading] = useState(false);
    const isSerbia = Helper.getCountryCode() === 'RS';
    const columns: ExtendedGridColDef[] = [
        { field: 'refundId', headerName: 'Id' },
        {
            field: 'date',
            headerName: t('general.date'),
            flex: 1,
            renderCell: (params) => {
                return (
                    <>
                        <Tooltip title={TimeHelper.formatDateHour(params.row.createDate)}>
                            <div className={PRLV2.cellDate}>{TimeHelper.formatDateHour(params.row.createDate)}</div>
                        </Tooltip>
                    </>
                );
            },
        },
        { field: 'accountOwner', headerName: t('general.name'), flex: 1 },
        {
            field: 'orderNumber',
            headerName: t('general.orderNo'),
            flex: 1,
            renderCell: (params) => {
                return (
                    <Tooltip title={params.row.orderNumber}>
                        <div className={PRLV2.cellOrderNumber}>{params.row.orderNumber}</div>
                    </Tooltip>
                );
            },
        },
        {
            field: 'refundReasonDesc',
            headerName: t('refundList.refundReasonDesc'),
            flex: 1,
            renderCell: (params) => {
                return (
                    <Tooltip title={Helper.getRefundReasonName(params.row.refundReasonId)}>
                        <div className={PRLV2.cellRefundReason}>{Helper.getRefundReasonName(params.row.refundReasonId)}</div>
                    </Tooltip>
                );
            },
        },
        {
            field: 'refundStatusName',
            headerName: t('refundList.refundStatusName'),
            flex: 1,
            renderCell: (params) => {
                return (
                    <Tooltip title={Helper.getRefundStatusName(params.row.statusId)}>
                        <div className={PRLV2.cellRefundStatus}>{Helper.getRefundStatusName(params.row.statusId)} </div>
                    </Tooltip>
                );
            },
        },
        {
            field: 'description',
            headerName: t('general.description'),
            flex: 1,
            renderCell: (params) => {
                return (
                    <Tooltip title={params.row.description}>
                        <div className={PRLV2.cellDescription}>{params.row.description}</div>
                    </Tooltip>
                );
            },
        },
        {
            field: 'wireRefundAmount',
            headerName: t('lcwMoney.amount'),
            renderCell: (params) => {
                return (
                    <Tooltip
                        title={
                            <p>
                                {params.row.wireRefundAmount && Helper.formatNumber(params.row.wireRefundAmount)}{' '}
                                {params.row.symbol}
                            </p>
                        }
                    >
                        <div className={PRLV2.cellAmount}>
                            {params.row.wireRefundAmount && Helper.formatNumber(params.row.wireRefundAmount)}{' '}
                            {params.row.symbol}
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            field: 'edit',
            headerName: ' ',
            flex: 1,
            renderCell: (params) => {
                return (
                    <p
                        className={[PRLV2.paymentDetailLink, 'linktxt'].join(' ')}
                        onClick={() => window.open(backofficeUrl + '/RefundV2/?id=' + params?.row.refundId, '_blank')}
                    >
                        {t('general.paymentDetail')}
                    </p>
                );
            },
        },
    ];

    const CreatePetition = () => {
        const refundIds = selectedRows.map((item) => item.refundId);
        RefundRestQuery.CreatePetitionV2({ RefundIdList: refundIds })
            .then((res: any) => {
                if (res && res.size > 0) {
                    const blob = new Blob([res], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${t('refundList.petitionRefundList')}_${TimeHelper.formatDateHour(
                        new Date()
                    )}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    setTimeout(() => {
                        URL.revokeObjectURL(url);
                    }, 1000);

                    Swal.fire({
                        icon: 'success',
                        title: t('general.completed'),
                        showConfirmButton: false,
                        timer: 1500,
                    });
                    SearchRefund();
                } else Swal.fire(t('general.failed'), ' ', 'error');
            })
            .finally(() => {
                setPdfDownload(false);
            });
        setCreatePetitionButtonDisabled(true);
    };

    const DownloadSerbiaBankTxt = () => {
        const refundIds = selectedRows.map((item) => item.refundId);
        setBankTxtDownloading(true);
        RefundRestQuery.ExportSerbiaBankTxt({ RefundIdList: refundIds })
            .then((res: any) => {
                if (res && res.size > 0) {
                    const blob = new Blob([res], { type: 'text/plain;charset=utf-8' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'PetitionRefundList.txt';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setTimeout(() => {
                        URL.revokeObjectURL(url);
                    }, 1000);
                } else {
                    Swal.fire(t('general.failed'), ' ', 'error');
                }
            })
            .catch(() => {
                Swal.fire(t('general.failed'), ' ', 'error');
            })
            .finally(() => {
                setBankTxtDownloading(false);
            });
    };

    const buildExcel = () => {
        setCreatePetitionButtonDisabled(false);
        return selectedRowsForDownload;
    };

    const handleSelectionModelChange = (model: any[]) => {
        const allRows = Array.isArray(datas) ? datas : [];
        const selectedIds = normalizeSelectionModelIds(model, allRows, selectedRowIds);
        const selectedIdsAsStrings = new Set(Array.from(selectedIds).map((id: any) => String(id)));
        const selectedData = allRows.filter((row: any) => selectedIdsAsStrings.has(String(row.refundId)));
        const normalizedSelectedRowIds = selectedData.map((row: any) => row.refundId);

        setSelectedForDownload(
            selectedData.map((item: any, index: number) => ({
                petitionList_no: index + 1,
                petitionList_customerId: item.customerId,
                petitionList_orderNo: item.orderNumber,
                petitionList_country: item.countryId,
                petitionList_recipient: item.accountOwner,
                petitionList_bankName: item.bankName,
                petitionList_bankAccountNumber: item.accountNumber,
                petitionList_ibanNumber: item.ibanNumber,
                petitionList_bankCode: item.bankCode,
                petitionList_bankAddressLine: item.bankAddressLine,
                petitionList_accountFatherName: item.accountFatherName,
                petitionList_taxNumber: item.taxNumber,
                petitionList_customerEmail: item.customerMail,
                petitionList_returnRequestDate: item.createDate && TimeHelper.formatDate(item.createDate),
                petitionList_symbol: item.symbol,
                petitionList_refund_reason_desc: item.refundReasonDesc,
                petitionList_amount: item.wireRefundAmount && Helper.formatNumber(item.wireRefundAmount),
                petitionList_refundId: item.refundId,
                petitionList_paymentReason: Helper.getRefundReasonName(item.refundReasonId),
                petitionList_description: item.description,
                petitionList_nvifId: item.nvifid ?? '',
            }))
        );

        setSelectedRowsAmount(
            Helper.formatNumber(selectedData.reduce((acc: any, item: any) => acc + (item.wireRefundAmount || 0), 0))
        );
        setSelectedRowIds(normalizedSelectedRowIds);
        setSelectedRows(selectedData);
    };

    const SearchRefund = () => {
        setLoading(true);

        RefundRestQuery.PetitionRefundlist(filterInput)
            .then((res: any) => {
                if (res.items) {
                    setHiddenExcelColumn(res.hiddenExcelColumnList);
                    setDatas(res.items);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        SearchRefund();
    }, []);

    useEffect(() => {
        if (pdfDownload && selectedRowsForDownload) {
            const pdf = <PetitionPDF data={selectedRowsForDownload} amount={selectedRowsAmount} />;
            setPdfComponent(pdf);
        }
    }, [pdfDownload, selectedRowsForDownload]);

    return (
        <SCUIContent className={PRLV2.page}>
            <div className={PRLV2.breadcrumbSection}>
                <SCUIBreadCrumb title={t('general.recordsCreatePetition')} componentLink={'PetitionRefundList'} />
            </div>
            <Grid className={PRLV2.contentLayout} container spacing={1}>
                <div className={PRLV2.pdfSection}>{pdfComponent}</div>

                {(isSerbia || (selectedRows && selectedRows.length)) ? (
                    <React.Fragment>
                        <Grid
                            className={PRLV2.actionsRow}
                            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}
                            size={{
                                lg: 12,
                                md: 12,
                                xs: 12,
                            }}
                        >
                            {isSerbia && (
                                <SCUIButton
                                    className={PRLV2.downloadBankTxtButton}
                                    text={t('refundList.downloadBankTxt')}
                                    disabled={!selectedRows?.length || bankTxtDownloading}
                                    type="primary"
                                    sizeType="fixed"
                                    size={'l'}
                                    buttonType="button"
                                    showStartIcon
                                    startIcon={<FileDownloadIcon sx={{ color: '#fff' }} />}
                                    onClick={DownloadSerbiaBankTxt}
                                />
                            )}
                            {selectedRows && selectedRows.length ? (
                                <>
                                    <div className={PRLV2.downloadButtonWrap}>
                                        <SCUIDownloadButton
                                            position="right"
                                            excelData={{
                                                excelDownloadData: selectedRowsForDownload,
                                                excelDownloadFolderName: t('refundList.petitionRefundList'),
                                                excelDownloadButtonText: t('refundList.createTable'),
                                                hiddenExcelColumn: hiddenExcelColumn,
                                            }}
                                            onclick={buildExcel}
                                        />
                                    </div>
                                    <SCUIButton
                                        className={PRLV2.createPetitionButton}
                                        text={t('refundList.createPetition')}
                                        disabled={createPetitionButtonDisabled}
                                        type="primary"
                                        sizeType="fixed"
                                        size={'m'}
                                        buttonType="button"
                                        onClick={CreatePetition}
                                    />
                                </>
                            ) : null}
                        </Grid>
                    </React.Fragment>
                ) : (
                    <> </>
                )}
                {datas ? (
                    <Grid
                        className={PRLV2.dataGridSection}
                        size={{
                            lg: 12,
                            md: 12,
                            xs: 12,
                        }}
                    >
                        <div className={PRLV2.dataGridWrap}>
                            <SCUICustomDataGrid
                                getRowId={(row: any) => row.refundId}
                                selectionOnClick={true}
                                checkboxSelection
                                rows={datas}
                                columns={columns}
                                onSelectionModelChange={handleSelectionModelChange}
                                selectionModel={selectedRowIds}
                                loading={loading}
                                onPageSizeChange={(i: number) => {
                                    setPageSize(i);
                                    setSelectedRows([]);
                                    setSelectedRowIds([]);
                                }}
                                onPageChange={(i: number) => {
                                    setPageNumber(i);
                                    setSelectedRows([]);
                                    setSelectedRowIds([]);
                                }}
                                page={pageNumber}
                                pageSize={pageSize}
                            />
                        </div>
                    </Grid>
                ) : loading ? (
                    <div className={PRLV2.loadingWrap}>
                        <SCUILoading />
                    </div>
                ) : (
                    <div className={PRLV2.emptyWrap}>
                        <NoResultFoundComponent errorText={t('general.noDataFound')} />
                    </div>
                )}
            </Grid>
        </SCUIContent>
    );
};

const normalizeSelectionModelIds = (model: any, allRows: any[], previousSelectedRowIds: any[]) => {
    if (Array.isArray(model)) {
        // SCUICustomDataGrid currently converts MUI v8 "exclude" model to [].
        // Treat this edge case as "select all" when user had partial/none selection.
        if (
            model.length === 0 &&
            allRows.length > 0 &&
            previousSelectedRowIds.length < allRows.length &&
            previousSelectedRowIds.length !== allRows.length
        ) {
            return new Set(allRows.map((row: any) => row.refundId));
        }
        return new Set(model);
    }

    // Supports MUI include/exclude row selection model shape.
    if (model && typeof model === 'object' && model.ids) {
        const ids = model.ids instanceof Set ? model.ids : new Set(model.ids);

        if (model.type === 'exclude') {
            const excludedIds = ids;
            return new Set(allRows.map((row: any) => row.refundId).filter((id: any) => !excludedIds.has(id)));
        }

        return ids;
    }

    return new Set();
};
